import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDDPSubscription } from '@zedoc/ddp-connector';
import {
  apiAdminAcquireProjectEditLock,
  apiAdminHeartbeatProjectEditLock,
  apiAdminOneProjectEditLock,
  apiAdminReleaseProjectEditLock,
  apiAdminRequestProjectEditTakeover,
  apiAdminRespondProjectEditTakeover,
} from '../../../common/api/admin';
import { default as ProjectDraftEditLockSelect } from '../../../common/selectors/ProjectDraftEditLock';
import CurrentUser from '../../../models/CurrentUser';
import { callMethod } from '../../../common/utilsClient/ddp/actions';
import {
  PROJECT_EDIT_LOCK_HEARTBEAT_INTERVAL_MS,
  PROJECT_EDIT_LOCK_IDLE_CHECK_INTERVAL_MS,
  PROJECT_EDIT_LOCK_IDLE_MS,
} from '../../../common/constants';
import { notifyError, notifySuccess } from '../../../utils/notify';
import { getProjectWizardEditorSessionId } from '../utils/projectWizardEditorSession';

const SAME_USER_EDIT_MESSAGE =
  'This project is already open in edit mode in the same user in another tab or window.';
const IDLE_EDIT_SESSION_ENDED_MESSAGE =
  'Your edit session ended due to inactivity. The project is now view-only.';
const IDLE_EDIT_SESSION_ENDED_WITH_DRAFT_MESSAGE =
  'Your edit session ended due to inactivity. The project is now view-only. Your changes were saved to draft.';

const useProjectWizardEditLock = ({
  projectId,
  isEditMode,
  setIsEditMode,
  canEditProject,
  onSaveDraft,
  onReloadProject,
  onCheckPendingChanges,
}) => {
  const dispatch = useDispatch();
  const editorSessionId = useMemo(() => getProjectWizardEditorSessionId(), []);
  const currentUserId = useSelector(CurrentUser.select.userId());
  const lockLostNotifiedRef = useRef(false);
  const wasWaitingForTakeoverRef = useRef(false);
  // Set while this editor is voluntarily handing the lock off, so the
  // involuntary "… took over this project" notification is not shown to them.
  const isHandingOffRef = useRef(false);
  const isIdleExpiringRef = useRef(false);
  const idleExpiryInProgressRef = useRef(false);
  const lastActivityAtRef = useRef(Date.now());
  const onCheckPendingChangesRef = useRef(onCheckPendingChanges);
  onCheckPendingChangesRef.current = onCheckPendingChanges;
  const onSaveDraftRef = useRef(onSaveDraft);
  onSaveDraftRef.current = onSaveDraft;
  const onReloadProjectRef = useRef(onReloadProject);
  onReloadProjectRef.current = onReloadProject;
  const [isRequestDialogVisible, setIsRequestDialogVisible] = useState(false);
  const [isRequestingTakeover, setIsRequestingTakeover] = useState(false);
  const [isRespondingTakeover, setIsRespondingTakeover] = useState(false);

  useDDPSubscription(
    projectId ? apiAdminOneProjectEditLock.withParams({ projectId }) : null,
  );

  const projectDraft = useSelector(
    ProjectDraftEditLockSelect.one().whereProjectIdEquals(() => projectId),
  );

  const hasActiveLock = projectDraft && projectDraft.hasActiveLock();
  const holderUserId = hasActiveLock ? projectDraft.getHolderUserId() : null;
  const holderDisplayName = hasActiveLock
    ? projectDraft.getHolderDisplayName()
    : '';
  const pendingTakeover = hasActiveLock
    ? projectDraft.getPendingTakeover()
    : null;

  const isHeldByMe =
    hasActiveLock &&
    holderUserId === currentUserId &&
    projectDraft.getEditorSessionId() === editorSessionId;

  const isHeldByOther = hasActiveLock && !isHeldByMe;

  const isWaitingForTakeoverApproval =
    !!pendingTakeover &&
    pendingTakeover.requesterUserId === currentUserId &&
    pendingTakeover.requesterEditorSessionId === editorSessionId;

  const incomingTakeoverRequest =
    isHeldByMe && pendingTakeover ? pendingTakeover : null;

  // NOTE: Mirror the current incoming request in a ref so `respondTakeover`
  //       can ignore stray invocations (e.g. the dialog's `hide` event firing
  //       on unmount after the request was already handled) instead of hitting
  //       the server again and surfacing a "No pending edit request" error.
  const incomingTakeoverRequestRef = useRef(null);
  incomingTakeoverRequestRef.current = incomingTakeoverRequest;

  const markUserActivity = useCallback(() => {
    lastActivityAtRef.current = Date.now();
  }, []);

  const lastMouseMoveActivityAtRef = useRef(0);
  const markUserActivityFromMouseMove = useCallback(() => {
    const now = Date.now();
    if (now - lastMouseMoveActivityAtRef.current < 1000) {
      return;
    }
    lastMouseMoveActivityAtRef.current = now;
    lastActivityAtRef.current = now;
  }, []);

  const releaseLock = useCallback(() => {
    if (!projectId || !canEditProject) {
      return Promise.resolve();
    }
    return dispatch(
      callMethod(apiAdminReleaseProjectEditLock, {
        projectId,
        editorSessionId,
      }),
    ).catch(() => undefined);
  }, [canEditProject, dispatch, editorSessionId, projectId]);

  const acquireLock = useCallback(() => {
    if (!projectId || !canEditProject) {
      return Promise.resolve({ acquired: false });
    }
    return dispatch(
      callMethod(apiAdminAcquireProjectEditLock, {
        projectId,
        editorSessionId,
      }),
    )
      .then((result) => {
        if (result.acquired) {
          lockLostNotifiedRef.current = false;
          setIsRequestDialogVisible(false);
        }
        return result;
      })
      .catch((err) => {
        notifyError()(err);
        return { acquired: false };
      });
  }, [canEditProject, dispatch, editorSessionId, projectId]);

  const tryEnterEditMode = useCallback(() => {
    if (hasActiveLock && holderUserId === currentUserId && !isHeldByMe) {
      notifyError()(new Error(SAME_USER_EDIT_MESSAGE));
      return Promise.resolve({ entered: false });
    }
    return acquireLock().then(({ acquired, lock }) => {
      if (acquired) {
        setIsEditMode(true);
        return { entered: true };
      }
      if (lock && lock.holderUserId === currentUserId) {
        notifyError()(new Error(SAME_USER_EDIT_MESSAGE));
        return { entered: false };
      }
      setIsRequestDialogVisible(true);
      return {
        entered: false,
        holderDisplayName:
          (lock && lock.holderDisplayName) || holderDisplayName,
      };
    });
  }, [
    acquireLock,
    currentUserId,
    hasActiveLock,
    holderDisplayName,
    holderUserId,
    isHeldByMe,
    setIsEditMode,
  ]);

  const requestTakeover = useCallback(() => {
    if (!projectId || !canEditProject) {
      return Promise.resolve();
    }
    setIsRequestingTakeover(true);
    return dispatch(
      callMethod(apiAdminRequestProjectEditTakeover, {
        projectId,
        editorSessionId,
      }),
    )
      .then(() => {
        notifySuccess('Edit access request sent.')();
        setIsRequestDialogVisible(true);
      })
      .catch(notifyError())
      .finally(() => setIsRequestingTakeover(false));
  }, [canEditProject, dispatch, editorSessionId, projectId]);

  const respondTakeover = useCallback(
    (accept) => {
      if (
        !projectId ||
        !canEditProject ||
        !incomingTakeoverRequestRef.current
      ) {
        return Promise.resolve();
      }
      setIsRespondingTakeover(true);
      const respond = () =>
        dispatch(
          callMethod(apiAdminRespondProjectEditTakeover, {
            projectId,
            editorSessionId,
            accept,
          }),
        );

      if (accept) {
        isHandingOffRef.current = true;
      }

      const chain = accept
        ? Promise.resolve()
            .then(() => (onSaveDraft ? onSaveDraft() : undefined))
            .then(() => respond())
            .then(() => {
              setIsEditMode(false);
              notifySuccess('Edit access handed off.')();
            })
        : respond().then(() => {
            notifySuccess('Edit access request denied.')();
          });

      return chain.catch(notifyError()).finally(() => {
        isHandingOffRef.current = false;
        setIsRespondingTakeover(false);
      });
    },
    [
      canEditProject,
      dispatch,
      editorSessionId,
      onSaveDraft,
      projectId,
      setIsEditMode,
    ],
  );

  // NOTE: When the current editor accepts a takeover the server transfers the
  //       lock and clears `pendingTakeover` in a single update, so
  //       `isWaitingForTakeoverApproval` is already false by the time we become
  //       the holder. We track that we *were* waiting to detect both the
  //       granted (we became holder) and denied (request cleared) outcomes.
  useEffect(() => {
    if (isEditMode) {
      return undefined;
    }
    if (isWaitingForTakeoverApproval) {
      wasWaitingForTakeoverRef.current = true;
      return undefined;
    }
    if (!wasWaitingForTakeoverRef.current) {
      return undefined;
    }

    if (isHeldByMe) {
      wasWaitingForTakeoverRef.current = false;
      setIsRequestDialogVisible(false);
      let cancelled = false;
      lockLostNotifiedRef.current = false;
      setIsEditMode(true);
      Promise.resolve(
        onReloadProjectRef.current ? onReloadProjectRef.current() : undefined,
      )
        .then(() => {
          if (cancelled) {
            return;
          }
          notifySuccess('Edit access granted.')();
        })
        .catch((err) => {
          if (cancelled) {
            return;
          }
          notifyError()(err);
        });
      return () => {
        cancelled = true;
      };
    }

    // Denied: another user still holds the lock after the request was cleared.
    if (hasActiveLock && holderUserId !== currentUserId) {
      wasWaitingForTakeoverRef.current = false;
      setIsRequestDialogVisible(false);
      notifyError()(new Error('Your edit access request was declined.'));
    }

    return undefined;
  }, [
    currentUserId,
    hasActiveLock,
    holderUserId,
    isEditMode,
    isHeldByMe,
    isWaitingForTakeoverApproval,
    setIsEditMode,
  ]);

  useEffect(() => {
    if (!isEditMode || !isHeldByOther) {
      return;
    }
    if (lockLostNotifiedRef.current) {
      return;
    }
    lockLostNotifiedRef.current = true;
    setIsEditMode(false);
    // Skip the notification when the editor is voluntarily handing off; they
    // already get the "Edit access handed off." confirmation.
    if (isHandingOffRef.current || isIdleExpiringRef.current) {
      return;
    }
    notifyError()(
      new Error(
        `Editing was ended because ${
          holderDisplayName || 'another user'
        } took over this project.`,
      ),
    );
  }, [holderDisplayName, isEditMode, isHeldByOther, setIsEditMode]);

  useEffect(() => {
    if (!isEditMode) {
      return undefined;
    }

    lastActivityAtRef.current = Date.now();
    lastMouseMoveActivityAtRef.current = Date.now();
    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, markUserActivity, { passive: true });
    });
    window.addEventListener('mousemove', markUserActivityFromMouseMove, {
      passive: true,
    });

    return () => {
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, markUserActivity);
      });
      window.removeEventListener('mousemove', markUserActivityFromMouseMove);
    };
  }, [isEditMode, markUserActivity, markUserActivityFromMouseMove]);

  useEffect(() => {
    if (!isEditMode || !projectId || !canEditProject) {
      return undefined;
    }

    const checkIdleExpiry = () => {
      if (idleExpiryInProgressRef.current) {
        return;
      }
      if (Date.now() - lastActivityAtRef.current < PROJECT_EDIT_LOCK_IDLE_MS) {
        return;
      }

      idleExpiryInProgressRef.current = true;
      const endIdleEditSession = (savedDraft) => {
        isIdleExpiringRef.current = true;
        return releaseLock().then(() => {
          setIsEditMode(false);
          notifyError({ useWarning: true })(
            new Error(
              savedDraft
                ? IDLE_EDIT_SESSION_ENDED_WITH_DRAFT_MESSAGE
                : IDLE_EDIT_SESSION_ENDED_MESSAGE,
            ),
          );
        });
      };

      Promise.resolve(
        onCheckPendingChangesRef.current
          ? onCheckPendingChangesRef.current()
          : { hasPendingChanges: false },
      )
        .then(({ hasPendingChanges }) => {
          if (hasPendingChanges && onSaveDraftRef.current) {
            return Promise.resolve(onSaveDraftRef.current()).then(() =>
              endIdleEditSession(true),
            );
          }
          return endIdleEditSession(false);
        })
        .catch(notifyError())
        .finally(() => {
          isIdleExpiringRef.current = false;
          idleExpiryInProgressRef.current = false;
        });
    };

    const idleIntervalId = window.setInterval(
      checkIdleExpiry,
      PROJECT_EDIT_LOCK_IDLE_CHECK_INTERVAL_MS,
    );

    return () => {
      window.clearInterval(idleIntervalId);
    };
  }, [canEditProject, isEditMode, projectId, releaseLock, setIsEditMode]);

  useEffect(() => {
    if (!isEditMode || !projectId || !canEditProject) {
      return undefined;
    }

    const sendHeartbeat = () => {
      dispatch(
        callMethod(
          apiAdminHeartbeatProjectEditLock,
          { projectId, editorSessionId },
          { noRetry: true },
        ),
      ).catch((err) => {
        if (err && err.error === 'forbidden') {
          setIsEditMode(false);
          notifyError()(
            new Error(
              'This project is being edited in another window. Your edit session was closed.',
            ),
          );
        }
      });
    };

    sendHeartbeat();
    const intervalId = window.setInterval(
      sendHeartbeat,
      PROJECT_EDIT_LOCK_HEARTBEAT_INTERVAL_MS,
    );

    const handlePageHide = () => {
      releaseLock();
    };
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('pagehide', handlePageHide);
      releaseLock();
    };
  }, [
    canEditProject,
    dispatch,
    editorSessionId,
    isEditMode,
    projectId,
    releaseLock,
    setIsEditMode,
  ]);

  return {
    projectDraft,
    holderDisplayName,
    isHeldByMe,
    isHeldByOther,
    isWaitingForTakeoverApproval,
    incomingTakeoverRequest,
    isRequestDialogVisible,
    setIsRequestDialogVisible,
    isRequestingTakeover,
    isRespondingTakeover,
    tryEnterEditMode,
    requestTakeover,
    respondTakeover,
    releaseLock,
  };
};

export default useProjectWizardEditLock;
